// 操作ログ閲覧画面 — 「操作ログ保存」PJ Ph.3
//
// AuditLog（Tier 1 = 自動記録 / Tier 2 = 明示記録）を検索・一覧する role 9 専用画面。
// サーバー（GET /api/auditLog）は ts 降順・ページング付きで返すため、
// 並び替えはサーバー側に委ね、本画面ではページ送りと絞り込みのみを行う。
//
// 配色・余白は tokens.css のデザイントークンのみを使用（新規 HEX・`.ant-*` 上書きなし）。

import React, { useCallback, useEffect, useState } from 'react';
import Axios from 'axios';
import { Table, Input, Select, Button, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Papa from 'papaparse';
import {
  PageContainer,
  PageHeader,
  StatusBadge,
  useResizableColumns,
  ellipsisColumn,
} from '../../common';
import { userRole } from '../../Config/userRole';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

// 1 ページの表示件数（サーバー側の既定と一致させる）
const PAGE_SIZE = 100;
// CSV ダウンロード時に取得する最大件数（サーバー側の上限と一致）
const CSV_LIMIT = 200;

// UTC で保存された日時を JST（UTC+9）の 'YYYY/MM/DD HH:mm:ss' で表示する。
// ブラウザのタイムゾーン設定に依存させないため utcOffset(9) を明示する。
const formatJst = (value) => {
  if (!value) return '-';
  return dayjs(value).utcOffset(9).format('YYYY/MM/DD HH:mm:ss');
};

// 記録の種別（1 = ミドルウェアの自動記録 / 2 = ルート内の明示記録）
const tierLabel = (tier) => {
  if (tier === 1) return '自動';
  if (tier === 2) return '詳細';
  return '-';
};

// HTTP ステータス → StatusBadge の意味カラー
const statusTone = (status) => {
  if (!status) return 'neutral';
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  if (status >= 200 && status < 300) return 'success';
  return 'neutral';
};

// 実行者の表示（未認証リクエストは行為者が解決できないため「（未認証）」）
const formatActor = (actor) => {
  if (!actor) return '（未認証）';
  const name = actor.name || '';
  const email = actor.email || '';
  if (!name && !email) return '（未認証）';
  if (name && email) return `${name}（${email}）`;
  return name || email;
};

// 操作対象。Tier 2 は target.label（当時の表示名）、無ければモデル名
const formatTarget = (target) => {
  if (!target) return '';
  return target.label || target.model || '';
};

// 操作内容の要約（Tier 2 のみ）。オブジェクトのまま出せないため JSON 文字列にする
const formatSummary = (summary) => {
  if (!summary) return '';
  try {
    return JSON.stringify(summary);
  } catch (error) {
    return '';
  }
};

// HTTP メソッドと経路
const formatHttp = (http) => {
  if (!http) return '';
  return `${http.method || ''} ${http.path || ''}`.trim();
};

function AuditLogPage() {
  // 絞り込み条件（入力中の値。検索ボタンで確定する）
  const [dateRange, setDateRange] = useState(null);
  const [email, setEmail] = useState('');
  const [action, setAction] = useState('');
  const [tier, setTier] = useState('all');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  // 現在の絞り込み条件をクエリパラメータへ変換する
  const buildParams = useCallback(
    (targetPage, limit) => {
      const params = { page: targetPage, limit };
      if (dateRange && dateRange[0]) {
        params.from = dateRange[0].format('YYYY-MM-DD');
      }
      if (dateRange && dateRange[1]) {
        params.to = dateRange[1].format('YYYY-MM-DD');
      }
      if (email.trim()) params.email = email.trim();
      if (action.trim()) params.action = action.trim();
      if (tier !== 'all') params.tier = tier;
      return params;
    },
    [dateRange, email, action, tier]
  );

  // 一覧取得（サーバー側でページング・ts 降順）
  const fetchLogs = useCallback(
    async (targetPage) => {
      setLoading(true);
      try {
        const response = await Axios.get('/api/auditLog', {
          params: buildParams(targetPage, PAGE_SIZE),
        });
        if (response.data.success) {
          setLogs(response.data.logs || []);
          setTotal(response.data.total || 0);
          setPage(targetPage);
        } else {
          message.error(response.data.error || '操作ログの取得に失敗しました');
        }
      } catch (error) {
        message.error(
          error.response?.data?.error || '操作ログの取得に失敗しました'
        );
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  // 初期表示（絞り込みなしの 1 ページ目）
  // ※ 依存に fetchLogs を入れると絞り込み入力のたびに再取得されるため意図的に初回のみ
  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 検索ボタン。条件を変えたら 1 ページ目から取得し直す
  const handleSearch = () => {
    fetchLogs(1);
  };

  // CSV ダウンロード。現在の絞り込み条件で最大 200 件を取得して書き出す
  const handleCsvDownload = async () => {
    setCsvLoading(true);
    try {
      const response = await Axios.get('/api/auditLog', {
        params: buildParams(1, CSV_LIMIT),
      });
      if (!response.data.success) {
        message.error(response.data.error || '操作ログの取得に失敗しました');
        return;
      }
      const rows = (response.data.logs || []).map((log) => ({
        日時: formatJst(log.ts),
        環境: log.env || '',
        種別: tierLabel(log.tier),
        実行者: formatActor(log.actor),
        role: log.actor && log.actor.role !== undefined ? log.actor.role : '',
        操作: log.action || '',
        対象: formatTarget(log.target),
        HTTP: formatHttp(log.http),
        結果: log.http && log.http.status ? log.http.status : '',
        要約: formatSummary(log.summary),
      }));
      if (rows.length === 0) {
        message.warning('ダウンロード対象の操作ログがありません');
        return;
      }
      const csv = Papa.unparse(rows);
      // Excel で文字化けしないよう UTF-8 BOM を付与する
      const blob = new Blob(['﻿' + csv], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `操作ログ_${dayjs().utcOffset(9).format('YYYYMMDD')}.csv`;
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      message.success(`${rows.length} 件をダウンロードしました`);
    } catch (error) {
      message.error(
        error.response?.data?.error || '操作ログの取得に失敗しました'
      );
    } finally {
      setCsvLoading(false);
    }
  };

  const rawColumns = [
    {
      title: '日時',
      dataIndex: 'ts',
      key: 'ts',
      width: 170,
      render: (value) => formatJst(value),
    },
    {
      title: '環境',
      dataIndex: 'env',
      key: 'env',
      width: 80,
    },
    {
      title: '種別',
      dataIndex: 'tier',
      key: 'tier',
      width: 80,
      render: (value) => tierLabel(value),
    },
    ellipsisColumn({
      title: '実行者',
      dataIndex: 'actor',
      key: 'actor',
      width: 240,
      render: (value) => formatActor(value),
    }),
    {
      title: 'role',
      dataIndex: 'actor',
      key: 'role',
      width: 140,
      render: (value) =>
        value && value.role !== undefined && value.role !== null
          ? `${value.role}：${userRole[value.role] || '-'}`
          : '-',
    },
    ellipsisColumn({
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 180,
    }),
    ellipsisColumn({
      title: '対象',
      dataIndex: 'target',
      key: 'target',
      width: 200,
      render: (value) => formatTarget(value),
    }),
    ellipsisColumn({
      title: 'HTTP',
      dataIndex: 'http',
      key: 'http',
      width: 260,
      render: (value) => formatHttp(value),
    }),
    {
      title: '結果',
      dataIndex: 'http',
      key: 'status',
      width: 100,
      render: (value) => {
        const status = value && value.status;
        return (
          <StatusBadge tone={statusTone(status)}>{status || '-'}</StatusBadge>
        );
      },
    },
    ellipsisColumn({
      title: '要約',
      dataIndex: 'summary',
      key: 'summary',
      width: 300,
      render: (value) => formatSummary(value),
    }),
  ];

  const { columns, components, scroll, wrapRef } =
    useResizableColumns(rawColumns);

  return (
    <PageContainer fluid style={{ paddingTop: 'var(--space-6)' }}>
      <PageHeader
        title="操作ログ"
        subtitle="ユーザー・管理者の操作履歴（保存期間 1 年）"
      />

      {/* 絞り込み条件 */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        <RangePicker
          value={dateRange}
          onChange={(value) => setDateRange(value)}
          placeholder={['開始日', '終了日']}
        />
        <Input
          placeholder="実行者メール（部分一致）"
          allowClear
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 240 }}
        />
        <Input
          placeholder="操作分類（前方一致。例 user.）"
          allowClear
          value={action}
          onChange={(e) => setAction(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 240 }}
        />
        <Select
          value={tier}
          onChange={(value) => setTier(value)}
          style={{ width: 180 }}
          options={[
            { value: 'all', label: '種別：すべて' },
            { value: '1', label: 'Tier1（自動）' },
            { value: '2', label: 'Tier2（詳細）' },
          ]}
        />
        <Button type="primary" onClick={handleSearch} loading={loading}>
          検索
        </Button>
        <Button onClick={handleCsvDownload} loading={csvLoading}>
          CSVダウンロード
        </Button>
      </div>

      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          scroll={scroll}
          dataSource={logs}
          loading={loading}
          size="middle"
          rowKey="_id"
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            showTotal: (count) => `全 ${count} 件`,
            onChange: (nextPage) => fetchLogs(nextPage),
          }}
        />
      </div>
    </PageContainer>
  );
}

export default AuditLogPage;
